import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';
import RevealProvider from '../components/RevealProvider';
import NewsletterSubscribe from '../components/NewsletterSubscribe';
import { POSTS, formatDate } from '../data/posts';

export default function Blog(){
  const posts = POSTS.slice().sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
  const [featured, ...rest] = posts;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Nomad Compass Journal",
    "url": "https://nomadcompasshub.com/blog",
    "blogPost": posts.map(p => ({
      "@type": "BlogPosting",
      "headline": p.title,
      "description": p.excerpt,
      "datePublished": p.publishedAt,
      "author": { "@type": "Organization", "name": "Nomad Compass" },
      "url": `https://nomadcompasshub.com/blog/${p.slug}`,
      "image": p.heroImage,
    }))
  };

  return (
    <RevealProvider>
      <Helmet>
        <title>The Nomad Compass Journal — Visa Analysis &amp; Relocation Playbooks</title>
        <meta name="description" content="Honest, data-backed writing on digital nomad visas, country-specific playbooks, and the structural trends reshaping remote work in 2026." />
        <link rel="canonical" href="https://nomadcompasshub.com/blog" />
        <meta property="og:title" content="The Nomad Compass Journal" />
        <meta property="og:description" content="Honest, data-backed writing on digital nomad visas." />
        <meta property="og:image" content={featured?.heroImage} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <TopBar variant="page" />

      <section className="blog-hero">
        <div className="blog-hero-inner reveal">
          <div className="eyebrow" style={{ justifyContent: 'center', display: 'inline-flex' }}>The Nomad Compass Journal</div>
          <h1>Essays and playbooks for the <em>globally mobile.</em></h1>
          <p>Honest, data-backed writing on digital nomad visas, country-specific playbooks, and the structural trends reshaping remote work.</p>
        </div>
      </section>

      {featured && (
        <section className="blog-featured reveal">
          <Link to={`/blog/${featured.slug}`} className="blog-featured-card" data-testid="featured-post">
            <div className="blog-featured-image">
              <img src={featured.heroImage} alt={featured.title} />
            </div>
            <div className="blog-featured-body">
              <div className="blog-meta">
                <span className="blog-pill">{featured.category}</span>
                <span>{formatDate(featured.publishedAt)}</span>
                <span>{featured.readMinutes} min read</span>
              </div>
              <h2>{featured.title}</h2>
              <p>{featured.deck || featured.excerpt}</p>
              <span className="blog-readmore">Read the essay →</span>
            </div>
          </Link>
        </section>
      )}

      {rest.length > 0 && (
        <section className="blog-grid-section">
          <div className="blog-grid-inner">
            <div className="blog-grid-head reveal">
              <h3>More from the Journal</h3>
            </div>
            <div className="blog-grid">
              {rest.map(p => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="blog-card reveal" data-testid={`post-${p.slug}`}>
                  <div className="blog-card-image">
                    <img src={p.heroImage} alt={p.title} loading="lazy" />
                  </div>
                  <div className="blog-card-body">
                    <div className="blog-meta">
                      <span className="blog-pill">{p.category}</span>
                      <span>{formatDate(p.publishedAt)}</span>
                      <span>{p.readMinutes} min</span>
                    </div>
                    <h4>{p.title}</h4>
                    <p>{p.excerpt}</p>
                    <span className="blog-readmore">Read →</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="newsletter-section">
        <div className="newsletter-inner">
          <NewsletterSubscribe source="blog" />
        </div>
      </section>

      <Footer />
    </RevealProvider>
  );
}
